import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, ExternalLink, Loader2, Plus } from 'lucide-react';
import { apiHelpers } from '@/lib/api';
import { apiErrorMessage } from '@/lib/conflict';
import { useConfirm } from '@/components/ui/confirm-provider';
import type { GoogleTagConnectStatus } from '@/types';

interface Props {
  projectId: string;
  /** Current plugin config — tagId tells us whether a tag is already set */
  currentConfig?: Record<string, any>;
  /** Live storefront origin, used to prefill the website URL when creating a property */
  storefrontOrigin?: string | null;
}

const GOOGLE_TAG = 'google-tag';
const connectionKey = (projectId: string) => ['plugin-google-tag-connection', projectId] as const;
const propertiesKey = (projectId: string) => ['oauth-properties', projectId, GOOGLE_TAG] as const;

/**
 * "Connect with Google" card for the Google Tag plugin overview.
 *
 * States:
 *   - platform has no Google OAuth client → renders nothing (paste still works)
 *   - no pending sign-in → Connect button (+ "replaces current tag" note)
 *   - pending sign-in (tokens stored, no property picked) → property picker
 *     with a "Create a new GA4 property" form and a Cancel that discards tokens
 *   - connected (googleConnection in config) → summary + Change
 *
 * Tokens live server-side only between sign-in and pick; they are revoked and
 * deleted the moment a tag ID is written.
 */
export function GoogleTagConnect({ projectId, currentConfig, storefrontOrigin }: Props) {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data, isPending, error } = useQuery({
    queryKey: connectionKey(projectId),
    queryFn: () => apiHelpers.googleTagConnection(projectId),
    staleTime: 30_000,
  });
  const status = data?.data?.data as GoogleTagConnectStatus | undefined;

  const invalidateAll = () =>
    queryClient.invalidateQueries({
      predicate: (q) =>
        q.queryKey.some((seg) => typeof seg === 'string' && seg.toLowerCase().includes('plugin')),
    });

  // Toasts for the round-trip back from Google (callback appends ?google=… or ?error=…)
  useEffect(() => {
    const google = searchParams.get('google');
    const err = searchParams.get('error');
    if (!google && !err) return;
    if (google === 'picked') toast.success('Google tag ID fetched and saved');
    else if (google === 'needs-pick') toast.info('Signed in to Google — pick a property below');
    else if (err === 'google_connect_failed') {
      toast.error(searchParams.get('description') || 'Could not connect to Google');
    } else if (err === 'access_denied') toast.info('Google sign-in cancelled');
    const next = new URLSearchParams(searchParams);
    ['google', 'error', 'description'].forEach((k) => next.delete(k));
    setSearchParams(next, { replace: true });
    if (google === 'picked') invalidateAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectMutation = useMutation({
    mutationFn: () => apiHelpers.googleTagConnect(projectId),
    onSuccess: (res) => {
      const url = res.data?.data?.redirectUrl;
      if (url) window.location.href = url;
      else toast.error('Google did not return a sign-in URL');
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Could not start Google sign-in')),
  });

  const cancelMutation = useMutation({
    mutationFn: () => apiHelpers.googleTagDisconnect(projectId),
    onSuccess: () => {
      toast.success('Google sign-in discarded');
      queryClient.invalidateQueries({ queryKey: connectionKey(projectId) });
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Could not discard sign-in')),
  });

  if (isPending) return null;
  if (error || !status || !status.available) return null;

  const tagId = typeof currentConfig?.tagId === 'string' ? currentConfig.tagId : '';

  // ── Pending pick: picker + create form ──────────────────────────────────
  if (status.pendingPick) {
    return (
      <PropertyPicker
        projectId={projectId}
        status={status}
        storefrontOrigin={storefrontOrigin}
        onDone={invalidateAll}
        onCancel={() => cancelMutation.mutate()}
        cancelling={cancelMutation.isPending}
      />
    );
  }

  const startConnect = async () => {
    if (
      tagId &&
      !(await confirm({
        title: 'Replace the current tag ID?',
        message: `Connecting Google will replace the current tag ID (${tagId}) with the GA4 measurement ID from your Google account.`,
      }))
    ) {
      return;
    }
    connectMutation.mutate();
  };

  // ── Connected summary ───────────────────────────────────────────────────
  const conn = status.connection;
  if (conn && conn.measurementId === tagId) {
    return (
      <div className="bg-card rounded-xl border p-5">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
          <div className="flex-1">
            <p className="font-medium">Connected to Google Analytics</p>
            <p className="text-muted-foreground mt-0.5 text-sm">
              {conn.propertyName} · {conn.accountName} · {conn.measurementId}
              {conn.email ? ` · ${conn.email}` : ''}
              {conn.source === 'created' ? ' · created by Tyashin' : ''}
            </p>
            <div className="mt-3 flex flex-wrap gap-3 text-sm">
              <button
                onClick={startConnect}
                disabled={connectMutation.isPending}
                className="text-primary hover:underline disabled:opacity-50"
              >
                Change property
              </button>
              <a
                href={`https://analytics.google.com/analytics/web/#/p${conn.propertyId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary inline-flex items-center gap-1 hover:underline"
              >
                Open in Google Analytics <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Not connected: the Connect card ─────────────────────────────────────
  return (
    <div className="bg-card rounded-xl border p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="flex-1">
          <p className="font-medium">
            {tagId ? 'Fetch a GA4 tag from Google' : 'Connect with Google'}
          </p>
          <p className="text-muted-foreground mt-0.5 text-sm">
            Sign in with Google and Tyashin fetches your GA4 measurement ID (G-…) — or creates a new
            Google Analytics property for this site — so you never copy it by hand.
            {tagId ? ` Replaces the current tag ID (${tagId}).` : ''} Google Ads (AW-) IDs still
            need to be pasted.
          </p>
        </div>
        <button
          onClick={startConnect}
          disabled={connectMutation.isPending}
          className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
        >
          {connectMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <GoogleGlyph />
          )}
          Connect with Google
        </button>
      </div>
    </div>
  );
}

// ─── Picker ──────────────────────────────────────────────────────────────────

function PropertyPicker({
  projectId,
  status,
  storefrontOrigin,
  onDone,
  onCancel,
  cancelling,
}: {
  projectId: string;
  status: GoogleTagConnectStatus;
  storefrontOrigin?: string | null;
  onDone: () => void;
  onCancel: () => void;
  cancelling: boolean;
}) {
  const [showCreate, setShowCreate] = useState(false);
  const [displayName, setDisplayName] = useState(status.suggested.displayName);
  const [websiteUrl, setWebsiteUrl] = useState(
    status.suggested.websiteUrl || storefrontOrigin || ''
  );

  const { data, isPending, error, refetch } = useQuery({
    queryKey: propertiesKey(projectId),
    queryFn: () => apiHelpers.getOAuthProperties(projectId, GOOGLE_TAG),
    staleTime: 60_000,
  });
  const payload = data?.data?.data;
  const accounts = payload?.accounts || [];
  const userInfo = payload?.userInfo;
  const total = accounts.reduce((n, a) => n + a.properties.length, 0);

  const selectMutation = useMutation({
    mutationFn: (propertyId: string) => apiHelpers.googleTagSelectProperty(projectId, propertyId),
    onSuccess: (res) => {
      toast.success(`Tag ${res.data?.data?.connection?.measurementId ?? ''} saved`);
      onDone();
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Could not save the property')),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      apiHelpers.googleTagCreateProperty(projectId, {
        displayName: displayName.trim(),
        websiteUrl: websiteUrl.trim(),
        timeZone: status.suggested.timeZone,
        currencyCode: status.suggested.currencyCode,
      }),
    onSuccess: (res) => {
      toast.success(
        `Property created — tag ${res.data?.data?.connection?.measurementId ?? ''} saved`
      );
      onDone();
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Could not create the property')),
  });

  const busy = selectMutation.isPending || createMutation.isPending || cancelling;

  return (
    <div className="space-y-3">
      {userInfo && (
        <div className="bg-muted/30 flex items-center gap-3 rounded-lg px-3 py-2 text-sm">
          {userInfo.picture ? (
            <img
              src={userInfo.picture}
              alt=""
              className="h-6 w-6 rounded-full"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="bg-primary/10 text-primary flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold">
              {userInfo.email[0]?.toUpperCase()}
            </div>
          )}
          <div className="flex-1">
            <span className="text-muted-foreground">Signed in as </span>
            <span className="font-medium">{userInfo.email}</span>
          </div>
          <button
            onClick={onCancel}
            disabled={busy}
            className="text-muted-foreground text-xs hover:underline disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      )}

      <div className="bg-card rounded-xl border p-5">
        {isPending ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="text-primary h-5 w-5 animate-spin" />
            <span className="text-muted-foreground ml-2 text-sm">
              Loading your Google Analytics properties…
            </span>
          </div>
        ) : error ? (
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-600" />
            <div className="flex-1">
              <p className="font-medium">Could not read your Google Analytics properties</p>
              <p className="text-muted-foreground mt-1 text-sm">
                {apiErrorMessage(error, 'Google API error')}
              </p>
              <div className="mt-3 flex gap-3 text-sm">
                <button onClick={() => refetch()} className="text-primary hover:underline">
                  Retry
                </button>
                <button onClick={onCancel} className="text-muted-foreground hover:underline">
                  Cancel and paste instead
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <p className="font-medium">
              {total > 0 ? 'Pick the property for this site' : 'No GA4 properties yet'}
            </p>
            <p className="text-muted-foreground mt-0.5 text-sm">
              {total > 0
                ? 'Its measurement ID becomes the Google tag on every page of your site.'
                : 'Create one below — Tyashin sets it up in your Google Analytics account and wires the tag.'}
            </p>

            {total > 0 && (
              <div className="mt-4 space-y-4">
                {accounts.map((acc) => (
                  <div key={acc.accountId}>
                    <p className="text-muted-foreground mb-2 text-xs font-semibold uppercase tracking-wide">
                      {acc.accountName}
                    </p>
                    <div className="space-y-2">
                      {acc.properties.map((prop) => (
                        <button
                          key={prop.propertyId}
                          onClick={() => selectMutation.mutate(prop.propertyId)}
                          disabled={busy || !prop.measurementId}
                          className="hover:bg-muted/50 flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm transition-colors disabled:opacity-40"
                        >
                          <div>
                            <p className="font-medium">{prop.displayName}</p>
                            <p className="text-muted-foreground mt-0.5 text-xs">
                              {prop.measurementId || 'No web data stream'}
                            </p>
                          </div>
                          {selectMutation.isPending &&
                            selectMutation.variables === prop.propertyId && (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            )}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-4 border-t pt-4">
              {!showCreate && total > 0 ? (
                <button
                  onClick={() => setShowCreate(true)}
                  disabled={busy}
                  className="text-primary inline-flex items-center gap-1 text-sm hover:underline disabled:opacity-50"
                >
                  <Plus className="h-3.5 w-3.5" /> Create a new GA4 property instead
                </button>
              ) : (
                <form
                  className="space-y-3"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!displayName.trim() || !/^https?:\/\//.test(websiteUrl.trim())) {
                      toast.error('Enter a property name and a full website URL (https://…)');
                      return;
                    }
                    createMutation.mutate();
                  }}
                >
                  <p className="text-sm font-medium">New GA4 property</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="text-sm">
                      <span className="text-muted-foreground mb-1 block text-xs">
                        Property name
                      </span>
                      <input
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        maxLength={100}
                        className="bg-background w-full rounded-lg border px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="text-sm">
                      <span className="text-muted-foreground mb-1 block text-xs">Website URL</span>
                      <input
                        value={websiteUrl}
                        onChange={(e) => setWebsiteUrl(e.target.value)}
                        placeholder="https://www.example.com"
                        className="bg-background w-full rounded-lg border px-3 py-2 text-sm"
                      />
                    </label>
                  </div>
                  <p className="text-muted-foreground text-xs">
                    Time zone {status.suggested.timeZone} · currency {status.suggested.currencyCode}{' '}
                    (from your project and team settings; change later in Google Analytics).
                  </p>
                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={busy}
                      className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50"
                    >
                      {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                      Create property and save tag
                    </button>
                    {total > 0 && (
                      <button
                        type="button"
                        onClick={() => setShowCreate(false)}
                        disabled={busy}
                        className="text-muted-foreground text-sm hover:underline"
                      >
                        Back
                      </button>
                    )}
                  </div>
                </form>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
      <path
        fill="currentColor"
        d="M21.35 11.1H12v2.9h5.35c-.25 1.4-1.6 4.1-5.35 4.1-3.2 0-5.85-2.65-5.85-5.9S8.8 6.3 12 6.3c1.85 0 3.05.8 3.75 1.45l2.55-2.45C16.7 3.8 14.55 2.8 12 2.8 6.95 2.8 2.85 6.9 2.85 12S6.95 21.2 12 21.2c5.3 0 8.8-3.7 8.8-8.95 0-.6-.05-1.05-.15-1.5z"
      />
    </svg>
  );
}
